"use client";

import { useEffect } from "react";
import { estErreurDeChunk, rechargerApresErreurDeChunk } from "@/lib/pwa/chunk-error";

/**
 * LOT 76 — Enregistre le Service Worker au montage de l'app.
 *
 * On ne s'enregistre qu'en production (en dev, le SW interfère avec le
 * HMR Next.js et empêche les recharges lors de modifs).
 *
 * Le SW est servi depuis `/sw.js` (fichier vanilla dans `public/sw.js`,
 * pas de build).
 *
 * ── Plus de rechargement automatique à la mise à jour du SW ──
 *
 * Avant, l'arrivée d'un nouveau SW (`controllerchange`) rechargeait la page.
 * Ce signal part à CHAQUE déploiement, quelques secondes après l'ouverture de
 * l'app — et aussi juste après un rechargement volontaire. Or la page venait
 * d'être servie par le réseau (pages et payloads RSC sont en network-first),
 * donc déjà à jour : le rechargement ne servait à rien, sinon à charger l'app
 * deux fois et à effacer une saisie en cours.
 *
 * Le seul vrai risque d'une page restée sur l'ancien code, c'est qu'elle
 * cherche un morceau qui n'existe plus : on recharge alors, une fois, au
 * moment où ça arrive (lib/pwa/chunk-error.ts).
 *
 * Le SW fait lui-même skipWaiting + clientsClaim à l'installation : le
 * nouveau prend la main tout seul, rien à lui demander d'ici.
 */
export function ServiceWorkerRegister() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (process.env.NODE_ENV !== "production") return;

    const onError = (e: ErrorEvent) => {
      if (estErreurDeChunk(e.error ?? e.message)) rechargerApresErreurDeChunk();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      if (estErreurDeChunk(e.reason)) rechargerApresErreurDeChunk();
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    let cancelled = false;
    if ("serviceWorker" in navigator) {
      // On enregistre une fois la page idle pour ne pas concurrencer le
      // chargement initial.
      const idle: typeof requestIdleCallback | undefined =
        (window as unknown as { requestIdleCallback?: typeof requestIdleCallback })
          .requestIdleCallback;
      const schedule = idle
        ? (cb: () => void) => idle(cb, { timeout: 4000 })
        : (cb: () => void) => setTimeout(cb, 1500);

      schedule(() => {
        if (cancelled) return;
        navigator.serviceWorker
          .register("/sw.js", { scope: "/" })
          .catch((err) => {
            console.warn("[sw] registration failed:", err);
          });
      });
    }

    return () => {
      cancelled = true;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
